/**
 * A janela do formulário (desenho "Indique a Eva", A127), sem navegação: quem
 * abre decide o que acontece ao fechar e ao enviar. A `AdhesionFormPage` a usa
 * pela rota `/eu/adhesion-form`; o aviso de bloqueio (14/10/2026) a abre por
 * cima de qualquer tela — inclusive o onboarding, que fica fora do portal.
 */
import { useEffect, useState } from 'react';
import { Loader } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconX } from '@tabler/icons-react';
import { formsApi, type CareForm } from '@eva/domain';
import { FormRenderer } from './FormRenderer';
import './profile.css';
import './formulario.css';

interface Props {
  formId: string;
  /** O chapéu quando o formulário não tem um (o nome do programa, na barreira). */
  programName?: string;
  /** Fechar sem enviar. Recebe a saída já animada. */
  onFechar: () => void;
  /** Depois de enviado e da animação de saída. */
  onEnviado: () => void | Promise<void>;
}

export function JanelaDoFormulario({ formId, programName = '', onFechar, onEnviado }: Props) {
  const [loadingForm, setLoadingForm] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formDef, setFormDef] = useState<CareForm | null>(null);
  const [saindo, setSaindo] = useState(false);

  useEffect(() => {
    let cancelado = false;
    if (!formId) {
      console.error('No formId provided');
      setLoadingForm(false);
      return;
    }
    setLoadingForm(true);
    formsApi
      .get(formId)
      .then((dados) => {
        if (!cancelado) setFormDef(dados);
      })
      .catch((error) => console.error('Failed to load form', error))
      .finally(() => {
        if (!cancelado) setLoadingForm(false);
      });
    return () => {
      cancelado = true;
    };
  }, [formId]);

  // Animação de saída (200 ms) antes de quem abriu seguir.
  const sair = (depois: () => void) => {
    setSaindo(true);
    window.setTimeout(depois, 200);
  };

  const enviar = async (data: Record<string, unknown>) => {
    try {
      setSubmitting(true);
      await formsApi.submit(formId, formDef?.version || 1, data);
      sair(() => void onEnviado());
      notifications.show({ color: 'green', message: 'Recebemos as suas respostas.' });
    } catch (error) {
      console.error('Error submitting form', error);
      // Antes a falha só ia para o console, e o botão voltava como se nada tivesse acontecido.
      notifications.show({ color: 'red', message: 'Não consegui enviar. Tente de novo.' });
    } finally {
      setSubmitting(false);
    }
  };

  const chapeu = formDef?.chapeu || programName;

  return (
    <div className="eva-adesao-fundo" data-saindo={saindo ? 'true' : 'false'}>
      <div className="eva-adesao-caixa" role="dialog" aria-modal="true" aria-labelledby="eva-form-titulo">
        <div className="eva-form-topo">
          <div className="eva-form-canto">
            <button type="button" className="eva-form-fechar" onClick={() => sair(onFechar)} aria-label="Fechar">
              <IconX size={18} stroke={1.6} />
            </button>
          </div>
          {chapeu && <span className="eva-form-chapeu">{chapeu}</span>}
          {!loadingForm && (
            <h2 className="eva-form-titulo" id="eva-form-titulo">
              {formDef?.title || programName || 'Formulário'}
            </h2>
          )}
          {formDef?.description && <p className="eva-form-descricao">{formDef.description}</p>}
        </div>

        {loadingForm ? (
          <div style={{ padding: '48px 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader size="md" color="var(--eva-brand-accent)" />
          </div>
        ) : formDef ? (
          <FormRenderer form={formDef} onSubmit={enviar} isSubmitting={submitting} />
        ) : (
          <div className="eva-form-corpo">
            <span>Não consegui abrir o formulário. Feche e tente de novo.</span>
          </div>
        )}
      </div>
    </div>
  );
}
